import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    if (next.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="change-password-modal"
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            className="relative w-full max-w-md bg-[#121216] border border-white/12 rounded-3xl p-6 md:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#1F5EFF]" />
                </div>
                <h3 className="font-display text-xl text-white">Change Password</h3>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {[
                { label: "Current password", val: current, set: setCurrent, tid: "cp-current" },
                { label: "New password", val: next, set: setNext, tid: "cp-new" },
                { label: "Confirm new password", val: confirm, set: setConfirm, tid: "cp-confirm" },
              ].map((f) => (
                <label key={f.tid} className="block">
                  <div className="font-mono-alt text-[10px] text-white/50 mb-1.5">{f.label}</div>
                  <input
                    type="password" required value={f.val} onChange={(e) => f.set(e.target.value)} data-testid={f.tid}
                    className="w-full bg-black/40 border border-white/10 focus:border-[#1F5EFF] rounded-xl px-4 py-2.5 outline-none text-sm text-white"
                  />
                </label>
              ))}
              {error && <div className="text-sm text-red-400" data-testid="cp-error">{error}</div>}
              <button type="submit" disabled={busy} data-testid="cp-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Update Password
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
